/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Observable, throwError as observableThrowError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';

// local imports
import { ApiResponse } from '@core/models/api-response.model';
import {
  IUiPermissionApiItem,
  IUserPermission,
  IUserPermissionGroup,
  USER_PERMISSION_PLATFORM,
  USER_PERMISSION_VALUE,
} from '@models/user-permission.model';
// services
import { ApiService } from 'app/services/api.service';
import { CLIENT_PLAN_TYPES } from '@core/models';
import { IUserPermissionType } from '@truckstop-tms/zuum-lib';

@Injectable({
  providedIn: 'root',
})
export class UserPermissionService {
  static mapClientPlanTypeToUserPermissionPlatform = {
    [CLIENT_PLAN_TYPES.TRUCKSTOP_CAPACITY_PRO]: USER_PERMISSION_PLATFORM.SHIPPER_WEB,
    [CLIENT_PLAN_TYPES.TRUCKSTOP_BUSINESS]: USER_PERMISSION_PLATFORM.SHIPPER_TMS,
    [CLIENT_PLAN_TYPES.TRUCKSTOP_ENTERPRISE]: USER_PERMISSION_PLATFORM.SHIPPER_TMS,

    [CLIENT_PLAN_TYPES.TRUCKSTOP_FLEET_MANAGER]: USER_PERMISSION_PLATFORM.CARRIER_TMS,

    [CLIENT_PLAN_TYPES.TRUCKSTOP_AUTOMATED_BROKER]: USER_PERMISSION_PLATFORM.ADMIN,
    [CLIENT_PLAN_TYPES.TRUCKSTOP_AUTOMATED_BROKER_PRO]: USER_PERMISSION_PLATFORM.ADMIN,
    // [CLIENT_PLAN_TYPES.TRUCKSTOP_AUTOMATED_BROKER_ENTERPRISE]: USER_PERMISSION_PLATFORM.ADMIN,
  };

  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) {}

  create(params: IUserPermission): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.userPermission}`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  updateById(id: string, params: IUserPermission): Observable<ApiResponse> {
    return this.http
      .put<ApiResponse>(`${this.apiService.userPermission}/${id}`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getForUserId(userId: string): Observable<ApiResponse<IUserPermission>> {
    return this.http
      .post<ApiResponse>(`${this.apiService.userPermission}/search-by-user`, { userId })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  searchGroupByTenantId(tenantId: string): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.userPermissionGroups}/search-by-tenant`, { tenantId })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getUIPermissionList(platform: USER_PERMISSION_PLATFORM): Observable<ApiResponse<IUiPermissionApiItem[]>> {
    return this.http
      .post<ApiResponse>(`${this.apiService.userPermission}/list-ui`, { platform })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  /**
   * given a list of UiPermissions (featureName/permissionName), compare it against a company uiPermissions list
   * and retrieve only the uiPermissions to which the company has been granted access to
   */
  filterAvailableUiPermissions(
    list: IUiPermissionApiItem[],
    companyActivePerms: IUserPermissionType[]
  ): IUiPermissionApiItem[] {
    const newList: IUiPermissionApiItem[] = [];

    if (!list) {
      return newList;
    }

    list.forEach((row: IUiPermissionApiItem) => {
      const matchingRow = companyActivePerms?.find((el: IUserPermissionType) => row.permissionName === el.name);
      if (matchingRow) {
        const hasAccess = matchingRow.value.includes(USER_PERMISSION_VALUE.READ);
        if (hasAccess) {
          newList.push(row);
        }
      }
    });
    return newList;
  }
}
